import { useState, useEffect } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { useSubscription } from "@/hooks/useSubscription";
import { useSupercalcCredits, TOKEN_PACKAGES } from "@/hooks/useSupercalcCredits";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { 
  Heart, Trash2, Sparkles, CreditCard, ExternalLink, Loader2, Download,
  Coins, AlertTriangle, Clock, CheckCircle, Info, Zap, Package, GraduationCap
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Favorite {
  id: string;
  calculator_path: string;
  calculator_name: string;
  created_at: string;
}

export default function Membership() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { role, hasAccess } = useUserRole();
  const { subscription, isLoading: isLoadingSubscription, getTierInfo, MEMBERSHIP_TIERS } = useSubscription();
  const { 
    totalCredits, monthlyAllocation, needsTopUp, purchases, 
    loading: loadingCredits, refetch: refetchCredits 
  } = useSupercalcCredits();
  
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [isLoadingFavorites, setIsLoadingFavorites] = useState(true);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [isPurchasingTokens, setIsPurchasingTokens] = useState<string | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('yearly');

  const isMember = hasAccess(["admin", "paid", "student"]);
  const isAdmin = hasAccess(["admin"]);
  const currentTier = getTierInfo();

  // Handle token purchase verification from URL
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const tokensPurchased = searchParams.get('tokens_purchased');
    const canceled = searchParams.get('canceled');

    if (canceled) {
      toast.info("Token purchase was canceled");
      // Clear the URL params
      navigate('/membership', { replace: true });
      return;
    }

    if (sessionId && tokensPurchased) {
      verifyTokenPurchase(sessionId, parseInt(tokensPurchased));
    }
  }, [searchParams]);

  const verifyTokenPurchase = async (sessionId: string, tokens: number) => {
    try {
      const { data, error } = await supabase.functions.invoke('verify-token-purchase', {
        body: { sessionId },
      });

      if (error) throw error;

      if (data?.success) {
        if (data.already_processed) {
          toast.info("This purchase was already processed");
        } else {
          toast.success(`${data.tokens_added} tokens added to your account!`);
          refetchCredits();
        }
      } else {
        toast.error(data?.error || "Failed to verify purchase");
      }
    } catch (error) {
      console.error("Error verifying purchase:", error);
      toast.error("Failed to verify token purchase");
    } finally {
      // Clear the URL params
      navigate('/membership', { replace: true });
    }
  };

  useEffect(() => {
    if (user) {
      fetchFavorites();
    }
  }, [user]);

  const fetchFavorites = async () => {
    if (!user) return;

    try {
      setIsLoadingFavorites(true);
      const { data, error } = await supabase
        .from("favorites")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setFavorites(data || []);
    } catch (error) {
      console.error("Error fetching favorites:", error);
    } finally {
      setIsLoadingFavorites(false);
    }
  };

  const removeFavorite = async (favoriteId: string) => {
    try {
      const { error } = await supabase.from("favorites").delete().eq("id", favoriteId);
      if (error) throw error;
      setFavorites(favorites.filter((f) => f.id !== favoriteId));
      toast.success("Removed from favorites");
    } catch (error) {
      console.error("Error removing favorite:", error);
      toast.error("Failed to remove favorite");
    }
  };

  const handleCancelMembership = async () => {
    if (!user) return;
    try {
      setIsProcessing(true);
      const { error } = await supabase
        .from("user_roles")
        .delete()
        .eq("user_id", user.id)
        .in("role", ["paid" as any, "student" as any]);
      if (error) throw error;
      toast.success("Membership cancelled successfully");
      setShowCancelDialog(false);
      window.location.reload();
    } catch (error) {
      console.error("Error cancelling membership:", error);
      toast.error("Failed to cancel membership");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    try {
      setIsProcessing(true);
      const { data, error } = await supabase.functions.invoke('delete-account', {
        method: 'POST',
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Failed to delete account');
      toast.success("Account deleted successfully");
      await supabase.auth.signOut();
      navigate("/");
    } catch (error) {
      console.error("Error deleting account:", error);
      toast.error("Failed to delete account. Please try again or contact support.");
    } finally {
      setIsProcessing(false);
      setShowDeleteDialog(false);
    }
  };

  const handleCheckout = async (priceId: string) => {
    if (!user) {
      toast.error("Please sign in to continue");
      navigate("/auth");
      return;
    }
    const isStudentTier = priceId === MEMBERSHIP_TIERS.student.yearly.priceId || priceId === MEMBERSHIP_TIERS.student.monthly.priceId;
    if (isStudentTier && !user.email?.toLowerCase().endsWith(".edu")) {
      toast.error("Student & Educator membership requires a .edu email address");
      return;
    }
    try {
      setIsCheckoutLoading(true);
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId },
      });
      if (error) throw error;
      if (data?.url) window.open(data.url, "_blank");
    } catch (error: any) {
      console.error("Error creating checkout session:", error);
      toast.error(error?.message || "Failed to start checkout process");
    } finally {
      setIsCheckoutLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!user) return;
    try {
      setIsProcessing(true);
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.url) window.open(data.url, "_blank");
    } catch (error) {
      console.error("Error opening customer portal:", error);
      toast.error("Failed to open subscription management");
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePurchaseTokens = async (packageId: string) => {
    if (!user) {
      toast.error("Please sign in to purchase tokens");
      navigate("/auth");
      return;
    }
    try {
      setIsPurchasingTokens(packageId);
      const { data, error } = await supabase.functions.invoke("purchase-tokens", {
        body: { packageType: packageId },
      });
      if (error) throw error;
      if (data?.url) window.open(data.url, "_blank");
    } catch (error: any) {
      console.error("Error purchasing tokens:", error);
      toast.error(error?.message || "Failed to start purchase");
    } finally {
      setIsPurchasingTokens(null);
    }
  };

  // Token Balance Card Component
  const TokenBalanceCard = () => (
    <Card className={needsTopUp ? "border-amber-500/50" : ""}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2">
          <Coins className="h-5 w-5 text-supercalc-brand" />
          SuperCalc Tokens
        </CardTitle>
        <CardDescription>Your AI query balance</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loadingCredits ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <span className="text-3xl font-bold">{totalCredits}</span>
              <span className="text-sm text-muted-foreground">tokens available</span>
            </div>
            
            {isMember && (
              <div className="text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <CheckCircle className="h-3 w-3 text-green-500" />
                  {monthlyAllocation} tokens/month included with membership
                </span>
              </div>
            )}

            {needsTopUp && (
              <div className="flex items-center gap-2 p-2 bg-amber-50 dark:bg-amber-950/30 rounded text-amber-700 dark:text-amber-400 text-sm">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>Low balance. Consider topping up below.</span>
              </div>
            )}

            {purchases.length > 0 && (
              <div className="pt-2 border-t">
                <p className="text-xs text-muted-foreground mb-2">Purchased tokens:</p>
                {purchases.slice(0, 2).map((purchase) => (
                  <div key={purchase.id} className="flex items-center justify-between text-xs">
                    <span>{purchase.tokens_remaining} remaining</span>
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Expires {new Date(purchase.expires_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );

  // Token Packages Card Component
  const TokenPackagesCard = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          Top Up Tokens
        </CardTitle>
        <CardDescription>
          Purchase additional tokens. Valid for 12 months.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="p-3 bg-muted/50 rounded-lg text-sm">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
            <p className="text-muted-foreground">
              <strong>Member pricing is designed to cover normal usage.</strong> Heavy users or those generating many documents may need to top up occasionally.
            </p>
          </div>
        </div>

        <div className="grid gap-3">
          {TOKEN_PACKAGES.map((pkg) => (
            <div 
              key={pkg.id} 
              className={`p-4 border rounded-lg flex items-center justify-between ${
                pkg.popular ? 'border-supercalc-brand bg-supercalc-brand/5' : ''
              }`}
            >
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{pkg.name}</span>
                  {pkg.popular && <Badge className="bg-supercalc-brand">Best Value</Badge>}
                  {pkg.savings && <Badge variant="secondary">Save {pkg.savings}</Badge>}
                </div>
                <p className="text-sm text-muted-foreground">
                  {pkg.tokens.toLocaleString()} tokens
                </p>
              </div>
              <div className="text-right">
                <p className="font-bold">${pkg.price}</p>
                <Button 
                  size="sm" 
                  variant={pkg.popular ? "default" : "outline"}
                  className={pkg.popular ? "bg-supercalc-brand hover:bg-supercalc-brand/90" : ""}
                  onClick={() => handlePurchaseTokens(pkg.id)}
                  disabled={isPurchasingTokens === pkg.id || !user}
                >
                  {isPurchasingTokens === pkg.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Buy"
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );

  // Determine current billing period based on subscription product
  const getCurrentBillingPeriod = (): 'monthly' | 'yearly' => {
    if (!subscription.productId) return 'yearly';
    if (subscription.productId === MEMBERSHIP_TIERS.student.monthly.productId ||
        subscription.productId === MEMBERSHIP_TIERS.paid.monthly.productId) {
      return 'monthly';
    }
    return 'yearly';
  };

  // Determine current tier type based on subscription product
  const getCurrentTierType = (): 'student' | 'paid' | null => {
    if (!subscription.productId) return null;
    if (subscription.productId === MEMBERSHIP_TIERS.student.yearly.productId ||
        subscription.productId === MEMBERSHIP_TIERS.student.monthly.productId) {
      return 'student';
    }
    if (subscription.productId === MEMBERSHIP_TIERS.paid.yearly.productId ||
        subscription.productId === MEMBERSHIP_TIERS.paid.monthly.productId) {
      return 'paid';
    }
    return null;
  };

  // Render different content based on user role/subscription
  const renderContent = () => {
    // Paid members (student, paid, or admin)
    if (isMember) {
      const currentBilling = getCurrentBillingPeriod();
      const currentTierType = getCurrentTierType();

      return (
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Current Plan Status */}
          <Card>
            <CardHeader>
              <CardTitle>Your Membership</CardTitle>
              <CardDescription>Manage your account and subscription</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {isLoadingSubscription ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : subscription.subscribed && currentTier ? (
                <div className="p-4 bg-accent/10 border border-accent rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-accent" />
                        Current Plan: {currentTier.name}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {currentTier.price} • Renews on {new Date(subscription.subscriptionEnd!).toLocaleDateString()}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Includes {monthlyAllocation} SuperCalc tokens/month
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={handleManageSubscription} disabled={isProcessing}>
                      <CreditCard className="h-4 w-4 mr-2" />
                      Billing
                    </Button>
                  </div>
                </div>
              ) : null}

              <div className="flex flex-wrap gap-2">
                <Button onClick={() => navigate("/supercalc")}>Open SuperCalc</Button>
                <Button variant="secondary" onClick={() => navigate("/install")} className="gap-2">
                  <Download className="h-4 w-4" />
                  Install App
                </Button>
                {isAdmin && (
                  <Button variant="outline" onClick={() => navigate("/admin")}>
                    Admin Panel
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Subscription Options - Switch Plans */}
          {subscription.subscribed && !isAdmin && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-primary" />
                  Change Your Plan
                </CardTitle>
                <CardDescription>
                  Switch between plans or billing periods. Changes take effect at your next billing date.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Billing Period Toggle */}
                <div className="flex items-center justify-center gap-4 p-4 bg-muted/30 rounded-lg">
                  <Label htmlFor="member-billing-toggle" className={billingPeriod === 'yearly' ? 'font-semibold' : 'text-muted-foreground'}>
                    Yearly <span className="text-accent text-xs ml-1">(Save 17%)</span>
                  </Label>
                  <Switch
                    id="member-billing-toggle"
                    checked={billingPeriod === 'monthly'}
                    onCheckedChange={(checked) => setBillingPeriod(checked ? 'monthly' : 'yearly')}
                  />
                  <Label htmlFor="member-billing-toggle" className={billingPeriod === 'monthly' ? 'font-semibold' : 'text-muted-foreground'}>
                    Monthly
                  </Label>
                </div>

                {/* Plan Options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Student & Educator Plan */}
                  <Card className={`border-2 ${currentTierType === 'student' && currentBilling === billingPeriod ? 'border-accent bg-accent/5' : ''}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2">
                          <GraduationCap className="h-4 w-4" />
                          Student & Educator
                        </CardTitle>
                        {currentTierType === 'student' && currentBilling === billingPeriod && (
                          <Badge variant="secondary" className="text-xs">Current</Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <p className="text-2xl font-bold">
                        ${billingPeriod === 'yearly' ? '44' : '4.40'}
                        <span className="text-sm font-normal text-muted-foreground">/{billingPeriod === 'yearly' ? 'year' : 'month'}</span>
                      </p>
                      <p className="text-xs text-muted-foreground">Requires .edu email</p>
                      {currentTierType === 'student' && currentBilling === billingPeriod ? (
                        <Button variant="outline" className="w-full" disabled>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Current Plan
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => handleCheckout(MEMBERSHIP_TIERS.student[billingPeriod].priceId)}
                          disabled={isCheckoutLoading || !user?.email?.toLowerCase().endsWith('.edu')}
                        >
                          {isCheckoutLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : null}
                          {currentTierType === 'student' ? 'Switch to ' + (billingPeriod === 'yearly' ? 'Yearly' : 'Monthly') : 'Switch Plan'}
                        </Button>
                      )}
                    </CardContent>
                  </Card>

                  {/* Standard Member Plan */}
                  <Card className={`border-2 ${currentTierType === 'paid' && currentBilling === billingPeriod ? 'border-accent bg-accent/5' : 'border-primary/30'}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">Standard Member</CardTitle>
                        {currentTierType === 'paid' && currentBilling === billingPeriod && (
                          <Badge variant="secondary" className="text-xs">Current</Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <p className="text-2xl font-bold">
                        ${billingPeriod === 'yearly' ? '88' : '8.80'}
                        <span className="text-sm font-normal text-muted-foreground">/{billingPeriod === 'yearly' ? 'year' : 'month'}</span>
                      </p>
                      <p className="text-xs text-muted-foreground">Priority support included</p>
                      {currentTierType === 'paid' && currentBilling === billingPeriod ? (
                        <Button variant="outline" className="w-full" disabled>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Current Plan
                        </Button>
                      ) : (
                        <Button
                          variant={currentTierType === 'student' ? 'default' : 'outline'}
                          className="w-full"
                          onClick={() => handleCheckout(MEMBERSHIP_TIERS.paid[billingPeriod].priceId)}
                          disabled={isCheckoutLoading}
                        >
                          {isCheckoutLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : null}
                          {currentTierType === 'paid' ? 'Switch to ' + (billingPeriod === 'yearly' ? 'Yearly' : 'Monthly') : (currentTierType === 'student' ? 'Upgrade' : 'Switch Plan')}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </div>

                <p className="text-xs text-muted-foreground text-center">
                  Plan changes are processed through Stripe. You'll be redirected to complete the change.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Token Balance */}
          <TokenBalanceCard />

          {/* Token Top-Up Options */}
          <TokenPackagesCard />

          {/* Favorites */}
          {favorites.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="h-5 w-5 text-accent fill-accent" />
                  Your Favorites
                </CardTitle>
                <CardDescription>Quick access to your favorite calculators</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2">
                  {favorites.map((favorite) => (
                    <Card key={favorite.id} className="p-3">
                      <div className="flex items-center justify-between">
                        <Link
                          to={favorite.calculator_path}
                          className="flex-1 text-sm font-medium hover:text-primary transition-colors"
                        >
                          {favorite.calculator_name}
                        </Link>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFavorite(favorite.id)}
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Account Management */}
          <Card>
            <CardHeader>
              <CardTitle>Account Management</CardTitle>
              <CardDescription>Manage your account settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <h3 className="font-medium">Signed in as</h3>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
                <Button variant="outline" onClick={() => supabase.auth.signOut()}>
                  Sign Out
                </Button>
              </div>

              {!isAdmin && subscription.subscribed && (
                <div className="space-y-2 pt-4 border-t">
                  <h3 className="font-medium">Cancel Subscription</h3>
                  <p className="text-sm text-muted-foreground">
                    Cancel automatic renewal. You'll retain access until {subscription.subscriptionEnd ? new Date(subscription.subscriptionEnd).toLocaleDateString() : 'end of your billing period'}. No refunds for remaining time.
                  </p>
                  <Button variant="outline" onClick={() => setShowCancelDialog(true)}>
                    Cancel Subscription
                  </Button>
                </div>
              )}

              <div className="pt-4 border-t space-y-2">
                <h3 className="font-medium text-destructive">Danger Zone</h3>
                <p className="text-sm text-muted-foreground">
                  Permanently delete your account and all associated data. This action cannot be undone.
                </p>
                <Button variant="destructive" onClick={() => setShowDeleteDialog(true)}>
                  Delete Account
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Free users - show pricing and subscription options
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Membership & Pricing</CardTitle>
            <CardDescription>Try SuperCalc free, then upgrade for full access</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Free Trial Info */}
            <div className="p-4 bg-muted/50 rounded-lg">
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-accent" />
                Free Trial Included
              </h3>
              <p className="text-sm text-muted-foreground">
                Every account gets <strong>5 free SuperCalc tokens</strong> to test the AI solver before upgrading. No credit card required!
              </p>
            </div>

            {/* Token-based Pricing Info */}
            <div className="p-4 bg-supercalc-brand/10 rounded-lg border border-supercalc-brand/20">
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <Coins className="h-4 w-4 text-supercalc-brand" />
                Token-Based Usage
              </h3>
              <p className="text-sm text-muted-foreground mb-2">
                SuperCalc uses AI tokens for each query. <strong>Membership includes monthly tokens designed to cover normal usage</strong>, meaning most users will never need to purchase additional tokens.
              </p>
              <p className="text-sm text-muted-foreground">
                Heavy users or those generating many AI documents can top up with affordable token packages.
              </p>
            </div>

            {/* Billing Period Toggle */}
            <div className="flex items-center justify-center gap-4 p-4 bg-muted/30 rounded-lg">
              <Label htmlFor="billing-toggle" className={billingPeriod === 'yearly' ? 'font-semibold' : 'text-muted-foreground'}>
                Yearly <span className="text-accent text-xs ml-1">(Save 17%)</span>
              </Label>
              <Switch
                id="billing-toggle"
                checked={billingPeriod === 'monthly'}
                onCheckedChange={(checked) => setBillingPeriod(checked ? 'monthly' : 'yearly')}
              />
              <Label htmlFor="billing-toggle" className={billingPeriod === 'monthly' ? 'font-semibold' : 'text-muted-foreground'}>
                Monthly
              </Label>
            </div>

            {/* Membership Benefits Highlight */}
            <div className="p-4 bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg border border-primary/20">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-primary" />
                What's Included with Membership
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="flex items-start gap-2">
                  <Coins className="h-4 w-4 text-supercalc-brand mt-0.5 flex-shrink-0" />
                  <span><strong>500 SuperCalc tokens/month</strong> for AI-powered problem solving</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Full LaTeX Learning Center</strong> with certification</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>AI document generator & LaTeX editor</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Future learning pathways included</span>
                </div>
              </div>
            </div>

            {/* Membership Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="border-2">
                <CardHeader>
                  <CardTitle className="text-lg">Student & Educator</CardTitle>
                  <CardDescription>Perfect for learners & teachers</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-3xl font-bold">
                    ${billingPeriod === 'yearly' ? '44' : '4.40'}
                    <span className="text-sm font-normal text-muted-foreground">/{billingPeriod === 'yearly' ? 'year' : 'month'}</span>
                  </p>
                  <ul className="text-sm space-y-1 text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <Coins className="h-3 w-3 text-supercalc-brand" />
                      <strong>500 tokens/month</strong> included
                    </li>
                    <li>✓ SuperCalc AI solver</li>
                    <li>✓ AI document generator</li>
                    <li className="flex items-center gap-2">
                      <GraduationCap className="h-3 w-3 text-primary" />
                      <strong>Full LaTeX Learning Center</strong>
                    </li>
                    <li>✓ All calculators & tools</li>
                    <li>✓ Favorites & Library</li>
                  </ul>
                  <p className="text-xs font-medium text-accent pt-2">Requires .edu email</p>
                  {user && (
                    <Button
                      className="w-full mt-4"
                      onClick={() => handleCheckout(MEMBERSHIP_TIERS.student[billingPeriod].priceId)}
                      disabled={isCheckoutLoading}
                    >
                      {isCheckoutLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <ExternalLink className="h-4 w-4 mr-2" />
                      )}
                      Subscribe
                    </Button>
                  )}
                </CardContent>
              </Card>

              <Card className="border-2 border-accent/50">
                <CardHeader>
                  <CardTitle className="text-lg">Standard Member</CardTitle>
                  <CardDescription>Full access for everyone</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-3xl font-bold">
                    ${billingPeriod === 'yearly' ? '88' : '8.80'}
                    <span className="text-sm font-normal text-muted-foreground">/{billingPeriod === 'yearly' ? 'year' : 'month'}</span>
                  </p>
                  <ul className="text-sm space-y-1 text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <Coins className="h-3 w-3 text-supercalc-brand" />
                      <strong>500 tokens/month</strong> included
                    </li>
                    <li>✓ Everything in Student & Educator</li>
                    <li>✓ Priority support</li>
                    <li>✓ Early feature access</li>
                    <li>✓ Support CalcLab development</li>
                  </ul>
                  {user && (
                    <Button
                      className="w-full mt-4"
                      onClick={() => handleCheckout(MEMBERSHIP_TIERS.paid[billingPeriod].priceId)}
                      disabled={isCheckoutLoading}
                    >
                      {isCheckoutLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <ExternalLink className="h-4 w-4 mr-2" />
                      )}
                      Subscribe
                    </Button>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Token Packages for non-members */}
            {user && (
              <div className="pt-4 border-t">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Or Buy Token Packs (No Subscription)
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Don't want a subscription? Buy tokens directly. Valid for 12 months.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {TOKEN_PACKAGES.map((pkg) => (
                    <div key={pkg.id} className="p-3 border rounded-lg text-center">
                      <p className="font-semibold">{pkg.tokens.toLocaleString()} tokens</p>
                      <p className="text-lg font-bold">${pkg.price}</p>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="mt-2 w-full"
                        onClick={() => handlePurchaseTokens(pkg.id)}
                        disabled={isPurchasingTokens === pkg.id}
                      >
                        {isPurchasingTokens === pkg.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          "Buy"
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!user && (
              <>
                <p className="text-muted-foreground">
                  Sign in or create an account to get started with your free trial.
                </p>
                <Button onClick={() => navigate("/auth")}>Sign In / Sign Up</Button>
              </>
            )}

            {user && (
              <p className="text-muted-foreground text-sm">
                Signed in as <span className="font-medium">{user.email}</span>
              </p>
            )}
          </CardContent>
        </Card>

        {/* Show favorites for free users */}
        {user && favorites.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="h-5 w-5 text-accent fill-accent" />
                Your Favorites
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2">
                {favorites.map((favorite) => (
                  <Card key={favorite.id} className="p-3">
                    <div className="flex items-center justify-between">
                      <Link
                        to={favorite.calculator_path}
                        className="flex-1 text-sm font-medium hover:text-primary transition-colors"
                      >
                        {favorite.calculator_name}
                      </Link>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFavorite(favorite.id)}
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Account Management for free users */}
        {user && (
          <Card>
            <CardHeader>
              <CardTitle>Account</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">{user.email}</p>
                <Button variant="outline" onClick={() => supabase.auth.signOut()}>
                  Sign Out
                </Button>
              </div>
              <div className="pt-4 border-t space-y-2">
                <h3 className="font-medium text-destructive">Danger Zone</h3>
                <Button variant="destructive" size="sm" onClick={() => setShowDeleteDialog(true)}>
                  Delete Account
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  return (
    <PageLayout>
      {renderContent()}

      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Subscription?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Cancelling your subscription will stop automatic renewal. You will:</p>
              <ul className="list-disc list-inside text-sm space-y-1 mt-2">
                <li>Retain full access until {subscription.subscriptionEnd ? new Date(subscription.subscriptionEnd).toLocaleDateString() : 'end of billing period'}</li>
                <li>Keep any purchased token packs until they expire</li>
                <li>Lose monthly token allocation after your current period ends</li>
              </ul>
              <p className="text-sm font-medium mt-3">No refunds are issued for the remaining subscription period.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isProcessing}>Keep Subscription</AlertDialogCancel>
            <AlertDialogAction onClick={handleManageSubscription} disabled={isProcessing}>
              {isProcessing ? "Opening..." : "Manage in Stripe Portal"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Account Permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. All your data, including any remaining tokens, will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isProcessing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={isProcessing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isProcessing ? "Deleting..." : "Delete Account"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageLayout>
  );
}
